import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LogOut,
  Map,
  ShieldCheck,
  Network,
  FileWarning,
  Menu,
  Users,
  X,
} from "lucide-react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { api, setToken } from "../lib/api";
import type { CurrentUser } from "../lib/types";

export default function AppLayout() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<CurrentUser>("/api/me"),
  });

  const onLogout = () => {
    setToken(null);
    navigate("/login");
  };

  const navItems = (
    <>
      <NavItem to="/" icon={<Map size={14} />} onNavigate={() => setDrawerOpen(false)}>
        Dashboard
      </NavItem>
      <NavItem to="/employees" icon={<Users size={14} />} onNavigate={() => setDrawerOpen(false)}>
        Employees
      </NavItem>
      <NavItem to="/org" icon={<Network size={14} />} onNavigate={() => setDrawerOpen(false)}>
        Org tree
      </NavItem>
      <NavItem
        to="/documents"
        icon={<FileWarning size={14} />}
        onNavigate={() => setDrawerOpen(false)}
      >
        Documents
      </NavItem>
      {me.data?.role === "super_admin" && (
        <NavItem
          to="/admin/users"
          icon={<ShieldCheck size={14} />}
          onNavigate={() => setDrawerOpen(false)}
        >
          Admin Users
        </NavItem>
      )}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-[var(--color-border)] bg-[var(--color-surface)]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-5 lg:px-8 h-14 flex items-center gap-3 sm:gap-6">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-200 hover:bg-slate-800 ring-1 ring-slate-700"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <Link to="/" className="flex items-center gap-2 text-sky-300 font-semibold">
            <span className="inline-flex w-7 h-7 rounded-md bg-sky-500/20 ring-1 ring-sky-400/30 items-center justify-center">
              <Map size={16} />
            </span>
            <span className="hidden sm:inline">Travel Tracking</span>
            <span className="sm:hidden">TT</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">{navItems}</nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3 text-sm">
            {me.data && (
              <div className="hidden sm:block text-right leading-tight">
                <div className="text-slate-200">{me.data.name}</div>
                <div className="text-xs text-slate-400">
                  {me.data.role === "super_admin" ? "Super Admin" : "HR Viewer"} · {me.data.sub}
                </div>
              </div>
            )}
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 ring-1 ring-slate-700"
              title="Sign out"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden bg-black/60 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[var(--color-surface)] ring-1 ring-[var(--color-border)] shadow-2xl p-3 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sky-300 font-semibold inline-flex items-center gap-2">
                <span className="inline-flex w-7 h-7 rounded-md bg-sky-500/20 ring-1 ring-sky-400/30 items-center justify-center">
                  <Map size={16} />
                </span>
                Travel Tracking
              </span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            {me.data && (
              <div className="rounded-md bg-slate-900/60 ring-1 ring-slate-800 px-3 py-2 mb-3">
                <div className="text-slate-200 text-sm">{me.data.name}</div>
                <div className="text-xs text-slate-400">
                  {me.data.role === "super_admin" ? "Super Admin" : "HR Viewer"} ·{" "}
                  {me.data.sub}
                </div>
              </div>
            )}
            <nav className="flex flex-col gap-1 text-sm">{navItems}</nav>
          </div>
        </div>
      )}

      <main className="flex-1 min-h-0 max-w-[1800px] w-full mx-auto px-3 sm:px-5 lg:px-8 py-3 sm:py-4 flex flex-col">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({
  to,
  icon,
  children,
  onNavigate,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end
      onClick={onNavigate}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 px-3 py-2 md:py-1.5 rounded-md ring-1 transition ${
          isActive
            ? "bg-sky-500/15 text-sky-200 ring-sky-400/30"
            : "text-slate-300 ring-transparent hover:bg-slate-800/60"
        }`
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}
